import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useEmpresas, type EmpresasFilter } from '@/api/queries/empresas';
import { useEmpresaUpdate } from '@/api/mutations/empresa';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { ScoreBadge, SinaisRow } from '@/components/ScoreBadge';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';
import { Search, ChevronRight, Phone, Globe, Camera, CheckCircle2, ArrowDown, Loader2, MessageCircle } from 'lucide-react';
import { cleanPhone, formatPhone, whatsappUrl } from '@/lib/phone';
import { displayName, secondaryName } from '@/lib/empresa';

const SETORES = [
  ['', 'todos'],
  ['saude', 'saúde'],
  ['educacao', 'educação'],
  ['industria', 'indústria'],
  ['comercio', 'comércio'],
  ['turismo', 'turismo'],
  ['imobiliario', 'imobiliário'],
  ['automotivo', 'automotivo'],
  ['agro', 'agro'],
  ['servicos', 'serviços'],
  ['tecnologia', 'tecnologia'],
  ['financeiro', 'financeiro'],
  ['outro', 'outro'],
] as const;

const SCORE_OPTS: Array<[string, string]> = [
  ['', 'qualquer'],
  ['3', '3+'],
  ['5', '5+'],
  ['7', '7+'],
  ['8', '8+'],
];

const STATUS_FUNIL_DEFAULT = 'lista';

function ToggleChip({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border/70 bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={2.25} />
      {label}
    </button>
  );
}

function formatBRL(raw?: number | string | null) {
  if (raw === undefined || raw === null || raw === '') return '—';
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(0)}k`;
  return `R$ ${n.toFixed(0)}`;
}

export function TriagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const update = useEmpresaUpdate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const filters = useMemo(() => {
    const f: EmpresasFilter = {
      status_funil: searchParams.get('status_funil') ?? STATUS_FUNIL_DEFAULT,
      order: 'score',
      limit: 500,
    };
    if (searchParams.get('q')) f.q = searchParams.get('q')!;
    if (searchParams.get('setor')) f.setor = searchParams.get('setor')!;
    if (searchParams.get('cidade')) f.cidade = searchParams.get('cidade')!;
    if (searchParams.get('cnae')) f.cnae = searchParams.get('cnae')!;
    if (searchParams.get('porte')) f.porte = searchParams.get('porte')!;
    if (searchParams.get('tem_telefone') === 'true') f.tem_telefone = true;
    if (searchParams.get('tem_site') === 'true') f.tem_site = true;
    if (searchParams.get('tem_redes') === 'true') f.tem_redes = true;
    if (searchParams.get('situacao_ativa') === 'true') f.situacao_ativa = true;
    if (searchParams.get('score_min')) f.score_min = parseInt(searchParams.get('score_min')!, 10);
    if (searchParams.get('enriquecida') === 'true') f.enriquecida = true;
    return f;
  }, [searchParams]);

  const { data, isLoading } = useEmpresas(filters);

  function setParam(key: string, value: string | boolean) {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value === false) next.delete(key);
    else next.set(key, String(value));
    setSearchParams(next, { replace: true });
  }
  function clearAll() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  function toggleSelect(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  function selectAll() {
    if (!data) return;
    setSelected(new Set(data.map((e) => e.id)));
  }
  function clearSelection() {
    setSelected(new Set());
  }

  async function bulkMove(status: string) {
    if (selected.size === 0) return;
    setBulkLoading(true);
    let ok = 0, fail = 0;
    for (const id of selected) {
      try {
        await update.mutateAsync({ id, payload: { status_funil: status as 'abertura' } });
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkLoading(false);
    toast.success(`${ok} movidas pra ${status}${fail ? ` · ${fail} falhas` : ''}`);
    setSelected(new Set());
  }

  const hasFilters =
    searchParams.size > 0 &&
    !(searchParams.size === 1 && searchParams.get('status_funil') === STATUS_FUNIL_DEFAULT);

  return (
    <div className="space-y-5">
      <div className="border-b border-border/70 pb-3">
        <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Triage</h1>
        <p className="mt-0.5 text-xs text-muted-foreground tabular">
          Empresas ainda não atacadas, ordenadas por score.{' '}
          <span className="mono">{data?.length ?? 0}</span> no resultado
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md min-w-0">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" strokeWidth={2.25} />
          <Input
            placeholder="buscar nome, razão social, CNAE, cidade..."
            value={searchParams.get('q') ?? ''}
            onChange={(e) => setParam('q', e.target.value)}
            className="pl-8"
          />
        </div>
        <select
          value={searchParams.get('setor') ?? ''}
          onChange={(e) => setParam('setor', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm"
        >
          {SETORES.map(([v, l]) => (<option key={v} value={v}>setor: {l}</option>))}
        </select>
        <select
          value={searchParams.get('score_min') ?? ''}
          onChange={(e) => setParam('score_min', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm"
        >
          {SCORE_OPTS.map(([v, l]) => (<option key={v} value={v}>score: {l}</option>))}
        </select>
        <Input
          placeholder="cidade"
          value={searchParams.get('cidade') ?? ''}
          onChange={(e) => setParam('cidade', e.target.value)}
          className="w-32"
        />
        {hasFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="h-9 rounded-md border border-border/70 px-3 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            limpar
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <ToggleChip
          active={searchParams.get('tem_telefone') === 'true'}
          onClick={() => setParam('tem_telefone', searchParams.get('tem_telefone') !== 'true')}
          icon={Phone}
          label="tem telefone"
        />
        <ToggleChip
          active={searchParams.get('tem_site') === 'true'}
          onClick={() => setParam('tem_site', searchParams.get('tem_site') !== 'true')}
          icon={Globe}
          label="tem site"
        />
        <ToggleChip
          active={searchParams.get('tem_redes') === 'true'}
          onClick={() => setParam('tem_redes', searchParams.get('tem_redes') !== 'true')}
          icon={Camera}
          label="tem redes"
        />
        <ToggleChip
          active={searchParams.get('situacao_ativa') === 'true'}
          onClick={() => setParam('situacao_ativa', searchParams.get('situacao_ativa') !== 'true')}
          icon={CheckCircle2}
          label="situação ativa"
        />
        <ToggleChip
          active={searchParams.get('enriquecida') === 'true'}
          onClick={() => setParam('enriquecida', searchParams.get('enriquecida') !== 'true')}
          icon={ArrowDown}
          label="só enriquecidas"
        />
      </div>

      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
          <span className="font-medium">
            <span className="mono">{selected.size}</span> selecionada{selected.size === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            limpar seleção
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => bulkMove('abertura')}
              disabled={bulkLoading}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronRight className="h-3 w-3" />}
              mover {selected.size} pra abertura
            </button>
            <button
              type="button"
              onClick={() => bulkMove('qualificacao')}
              disabled={bulkLoading}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-card px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
            >
              → qualificação
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma empresa encontrada com esses filtros.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border/70">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
              <tr className="border-b border-border/70">
                <th className="px-2 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selected.size === data.length}
                    onChange={(e) => (e.target.checked ? selectAll() : clearSelection())}
                  />
                </th>
                <th className="px-2 py-2 text-left">empresa</th>
                <th className="px-2 py-2 text-left">CNAE</th>
                <th className="px-2 py-2 text-left">cidade</th>
                <th className="px-2 py-2 text-left">telefone</th>
                <th className="px-2 py-2 text-left">porte</th>
                <th className="px-2 py-2 text-right">capital</th>
                <th className="px-2 py-2 text-center">sinais</th>
                <th className="px-2 py-2 text-right">score</th>
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <tr
                  key={e.id}
                  className={cn(
                    'border-b border-border/50 hover:bg-muted/40',
                    selected.has(e.id) && 'bg-primary/5',
                  )}
                >
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(e.id)}
                      onChange={() => toggleSelect(e.id)}
                    />
                  </td>
                  <td className="px-2 py-2 min-w-0">
                    <Link to={`/empresas/${e.id}`} className="font-medium hover:underline truncate block max-w-[280px]">
                      {displayName(e)}
                    </Link>
                    {secondaryName(e) && (
                      <span className="text-[11px] text-muted-foreground/80 truncate block max-w-[280px]">
                        {secondaryName(e)}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[11px] text-muted-foreground max-w-[200px] truncate" title={e.cnae_principal}>
                    {e.cnae_principal ?? '—'}
                  </td>
                  <td className="px-2 py-2 text-[12px] text-muted-foreground">
                    {e.cidade ?? '—'}{e.estado ? ` · ${e.estado}` : ''}
                  </td>
                  <td className="px-2 py-2 text-[12px]">
                    {e.telefone_receita ? (
                      <div className="inline-flex items-center gap-1.5 mono">
                        <a
                          href={`tel:${cleanPhone(e.telefone_receita)}`}
                          onClick={(ev) => ev.stopPropagation()}
                          className="text-emerald-700 hover:underline dark:text-emerald-400"
                        >
                          {formatPhone(e.telefone_receita)}
                        </a>
                        <a
                          href={whatsappUrl(e.telefone_receita)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(ev) => ev.stopPropagation()}
                          className="text-green-600 hover:opacity-80"
                          title="WhatsApp"
                        >
                          <MessageCircle className="h-3 w-3" strokeWidth={2.25} />
                        </a>
                      </div>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[11px] uppercase text-muted-foreground">
                    {e.porte ? e.porte.slice(0, 18) : '—'}
                  </td>
                  <td className="px-2 py-2 text-right mono text-[11px] tabular text-muted-foreground">
                    {formatBRL(e.capital_social_brl)}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex justify-center">
                      <SinaisRow e={e} />
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <ScoreBadge score={e.score_prospect} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
